import { useMemo, type ReactElement } from "react";

import { InfoTooltip } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { elementStateFormatStrings } from "./formatStrings.ts";
import type { ElementState } from "./types.ts";

/** The base element states available for all elements */
export const baseStates: ElementState[] = ["default", "hover", "focus", "active", "disabled"];

/** Element states for draggable elements */
export const draggableStates: ElementState[] = [
  "dragging",
  "dragPlaceholder",
  "draggingAllowed",
  "draggingNotAllowed",
  // TODO: need predicates on element states
  // "dragCorrect",
  // "dragIncorrect",
  "dragDropped",
];

/** Element states for droppable elements */
export const droppableStates: ElementState[] = [
  "dropTarget",
  "droppableAllowed",
  "droppableNotAllowed",
  "droppableOccupied",
  "droppableActive",
];

/** Element states for hot zone elements */
export const hotZoneStates: ElementState[] = ["hotZoneActive", "hotZoneAllowed", "hotZoneNotAllowed"];

const allStates = [...baseStates, ...draggableStates, ...droppableStates, ...hotZoneStates];

type StateLabel = { label: ReactElement; plainLabel: string };

export const useElementStateLabels = (): Record<ElementState, StateLabel> => {
  const formatMessage = useFormatMessage(elementStateFormatStrings);

  return useMemo(() => {
    return Object.fromEntries(
      allStates.map((state) => {
        const label = formatMessage(`${state}Label`);
        const tooltipKey = `${state}Tooltip` as const;
        // Tooltip is optional - check if it exists in formatStrings
        const tooltip = tooltipKey in elementStateFormatStrings ? formatMessage(tooltipKey) : undefined;
        return [
          state,
          {
            label: tooltip ? (
              <>
                {label} <InfoTooltip tooltip={tooltip} />
              </>
            ) : (
              label
            ),
            plainLabel: label,
          },
        ];
      }),
    ) as Record<ElementState, StateLabel>;
  }, [formatMessage]);
};

/**
 * Plain text label for when you don't need the tooltip (eg for displaying the selected value).
 */
export const useElementStatePlainLabels = (): Record<ElementState, string> => {
  const formatMessage = useFormatMessage(elementStateFormatStrings);

  return useMemo(() => {
    return Object.fromEntries(allStates.map((state) => [state, formatMessage(`${state}Label`)])) as Record<
      ElementState,
      string
    >;
  }, [formatMessage]);
};
